/**
 * Phase 3.36 — Dealer-Übersicht der eingegangenen Werkstatt-Termin-Buchungen.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Loader2, Calendar, Clock, Mail, Phone, Wrench, CheckCircle2, XCircle, CalendarClock, Bike, Truck } from "lucide-react";
import { toast } from "sonner";
import PublicBookingLink from "./workshop/PublicBookingLink";
import BookingExtrasBadges from "./workshop/BookingExtrasBadges";

// Jun-2026 (User-Wunsch) — Tagesübersicht: Termine mit Ersatzfahrzeug- und
// Abholaufträgen auf einen Blick, Datum frei wählbar.
function DayOverview() {
    const today = new Date().toISOString().slice(0, 10);
    const [date, setDate] = useState(today);
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/workshop/bookings/day-overview?date=${date}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null));
    }, [date]);
    const label = new Date(date).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" });
    return (
        <div className="bg-[#111827] text-white rounded-sm p-4" data-testid="workshop-day-overview">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#FFCC00]">Tagesübersicht</div>
                    <div className="font-display font-black text-lg">{date === today ? "Heute" : label}</div>
                </div>
                <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)}
                    className="h-8 rounded-sm border border-white/20 bg-white/10 text-white text-xs px-2 [color-scheme:dark]"
                    data-testid="day-overview-date" />
                <div className="flex gap-2">
                    {[
                        [data?.count ?? "–", "Termine", Calendar],
                        [data?.loaner_count ?? "–", "Ersatzfahrzeug", Bike],
                        [data?.pickup_count ?? "–", "Abholungen", Truck],
                    ].map(([n, l, Icon]) => (
                        <div key={l} className="bg-white/10 rounded-sm px-3 py-1.5 text-center min-w-[86px]" data-testid={`day-overview-${l.toLowerCase()}`}>
                            <div className="font-mono-num font-black text-xl leading-none flex items-center justify-center gap-1.5"><Icon className="w-4 h-4 text-[#FFCC00]" />{n}</div>
                            <div className="text-[9px] font-bold uppercase tracking-wider text-white/60 mt-0.5">{l}</div>
                        </div>
                    ))}
                </div>
            </div>
            {(data?.items || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="day-overview-list">
                    {data.items.slice(0, 10).map((b) => (
                        <span key={b.id} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 rounded-sm px-2 py-1 text-xs">
                            <span className="font-mono-num font-bold text-[#FFCC00]">{b.time_slot}</span>
                            <span className="font-bold truncate max-w-[130px]">{b.customer_name}</span>
                            <span className="text-white/60">{b.service_type}</span>
                            {b.loaner_requested && <Bike className="w-3.5 h-3.5 text-orange-400" />}
                            {b.pickup_service_requested && <Truck className="w-3.5 h-3.5 text-orange-400" />}
                        </span>
                    ))}
                    {data.count > 10 && <span className="text-xs text-white/50 self-center">+{data.count - 10} weitere</span>}
                </div>
            )}
            {data && data.count === 0 && <div className="mt-2 text-xs text-white/50">Keine Termine an diesem Tag.</div>}
        </div>
    );
}

function TomorrowDigestBanner() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/workshop/bookings/tomorrow")
            .then((r) => setData(r.data))
            .catch(() => setData({ count: 0, items: [] }));
    }, []);
    if (!data || data.count === 0) return null;
    const dt = data.date ? new Date(data.date) : null;
    const label = dt ? dt.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" }) : data.date;
    return (
        <div className="bg-[#FFCC00]/15 border-2 border-[#FFCC00] rounded-sm p-4" data-testid="workshop-tomorrow-banner">
            <div className="flex items-start gap-3 flex-wrap">
                <div className="bg-[#111827] text-[#FFCC00] w-12 h-12 rounded-sm flex items-center justify-center shrink-0">
                    <CalendarClock className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#111827]">Morgige Termine</div>
                    <div className="font-display font-black text-lg text-[#111827]" data-testid="workshop-tomorrow-headline">
                        Morgen erwarten Sie {data.count} Termin{data.count !== 1 ? "e" : ""}
                    </div>
                    <div className="text-xs text-gray-700">{label}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="workshop-tomorrow-list">
                        {data.items.slice(0, 8).map((b) => (
                            <span
                                key={b.id}
                                className="inline-flex items-center gap-1 bg-white border border-gray-300 rounded-sm px-2 py-1 text-xs"
                                data-testid={`workshop-tomorrow-chip-${b.id}`}
                            >
                                <Clock className="w-3 h-3 text-[#111827]" />
                                <span className="font-mono-num font-bold">{b.time_slot}</span>
                                <span className="text-gray-500">·</span>
                                <span className="font-bold truncate max-w-[120px]">{b.customer_name}</span>
                                <span className="text-gray-500">·</span>
                                <span className="text-gray-700">{b.service_type}</span>
                            </span>
                        ))}
                        {data.count > 8 && (
                            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider self-center">
                                +{data.count - 8} weitere
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function WorkshopBookings() {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("all");

    // Jun-2026 (User-Wunsch) — Partner-Antwort erfassen / erinnern + Mietvertrag
    const setPartnerResponse = async (b, response) => {
        try {
            await api.post(`/workshop/bookings/${b.id}/partner-response`, { response });
            toast.success(response === "accepted" ? "Zusage erfasst" : response === "declined" ? "Absage erfasst" : "Antwort zurückgesetzt");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    };
    const remindPartner = async (b) => {
        try {
            await api.post(`/workshop/bookings/${b.id}/partner-remind`);
            toast.success("Erinnerung an den Partner gesendet (bzw. zur E-Mail-Freigabe eingereicht)");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Erinnerung fehlgeschlagen"); }
    };
    const openRentalContract = async (b) => {
        try {
            const res = await api.get(`/dealer/rental-bookings/${b.loaner_rental_booking_id}/contract.pdf`, { responseType: "blob" });
            window.open(URL.createObjectURL(res.data), "_blank", "noopener");
        } catch { toast.error("Mietvertrag konnte nicht geladen werden"); }
    };

    const load = async () => {
        try {
            const r = await api.get("/workshop/bookings");
            setItems(r.data.items || []);
        } catch {
            setItems([]);
        }
    };
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await api.get("/workshop/bookings");
                if (active) setItems(r.data.items || []);
            } catch {
                if (active) setItems([]);
            }
        })();
        return () => { active = false; };
    }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/workshop/bookings/${id}/status`, { status });
            toast.success(status === "completed" ? "Als erledigt markiert" : status === "cancelled" ? "Buchung storniert" : "Status aktualisiert");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        }
    };

    if (items === null) {
        return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Buchungen werden geladen…</div>;
    }

    const filtered = items.filter((b) => filter === "all" || b.status === filter);

    return (
        <div className="space-y-4" data-testid="workshop-bookings">
            <DayOverview />
            <TomorrowDigestBanner />
            {/* Jul-2026 (User-Wunsch): Buchungslink-Block in den Werkstatt-Kalender verschoben. */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display font-bold text-2xl">Werkstatt-Termine</h2>
                    <p className="text-sm text-gray-600 mt-1">Eingehende Buchungen über Ihre öffentliche Plattform-Buchungsseite.</p>
                </div>
                <div className="flex gap-1">
                    {[
                        ["all", `Alle (${items.length})`],
                        ["confirmed", `Bestätigt (${items.filter((i) => i.status === "confirmed").length})`],
                        ["completed", `Erledigt (${items.filter((i) => i.status === "completed").length})`],
                        ["cancelled", `Storniert (${items.filter((i) => i.status === "cancelled").length})`],
                    ].map(([v, label]) => (
                        <button
                            key={v}
                            onClick={() => setFilter(v)}
                            className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider ${filter === v ? "bg-[#111827] text-white" : "bg-white border border-gray-300 text-gray-700 hover:border-[#111827]"}`}
                            data-testid={`bookings-filter-${v}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-12 border border-dashed border-gray-300 rounded-sm">
                    {items.length === 0 ? "Noch keine Buchungen erhalten." : "Keine Buchungen in dieser Kategorie."}
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map((b) => (
                        <div key={b.id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`booking-${b.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="inline-flex items-center gap-1 bg-[#FFCC00]/20 text-[#111827] border border-[#FFCC00] px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase tracking-wider">
                                            <Wrench className="w-3 h-3" /> {b.service_type}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${b.status === "confirmed" ? "bg-blue-100 text-blue-700" : b.status === "completed" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>
                                            {b.status === "confirmed" ? "Bestätigt" : b.status === "completed" ? "Erledigt" : "Storniert"}
                                        </span>
                                    </div>
                                    <div className="font-bold text-base">{b.customer_name}</div>
                                    <div className="flex flex-wrap gap-3 text-xs text-gray-700 mt-1">
                                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(b.date).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "long" })}</span>
                                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{b.time_slot}</span>
                                        <a href={`mailto:${b.customer_email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="w-3 h-3" />{b.customer_email}</a>
                                        {b.customer_phone && (
                                            <a href={`tel:${b.customer_phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="w-3 h-3" />{b.customer_phone}</a>
                                        )}
                                    </div>
                                    {(b.vehicle_brand || b.vehicle_model) && (
                                        <div className="text-xs text-gray-600 mt-1">
                                            <strong>Fahrzeug:</strong> {[b.vehicle_brand, b.vehicle_model].filter(Boolean).join(" ")}
                                        </div>
                                    )}
                                    {b.notes && <div className="text-xs text-gray-700 mt-1 italic">„{b.notes}&ldquo;</div>}
                                    {(b.loaner_requested || b.pickup_service_requested) && (
                                        <BookingExtrasBadges booking={b} onChanged={load} />
                                    )}
                                </div>
                                {b.status === "confirmed" && (
                                    <div className="flex flex-col gap-1 shrink-0">
                                        <Button
                                            size="sm"
                                            onClick={() => updateStatus(b.id, "completed")}
                                            className="bg-green-600 hover:bg-green-700 text-white rounded-sm font-bold text-xs uppercase tracking-wider"
                                            data-testid={`booking-complete-${b.id}`}
                                        >
                                            <CheckCircle2 className="w-3 h-3 mr-1" /> Erledigt
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => updateStatus(b.id, "cancelled")}
                                            className="rounded-sm font-bold text-xs uppercase tracking-wider"
                                            data-testid={`booking-cancel-${b.id}`}
                                        >
                                            <XCircle className="w-3 h-3 mr-1" /> Stornieren
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
